import React, { FC, useEffect, useMemo, useState } from 'react'
import { KTSVG } from '../../../../../_metronic/helpers'
import { toast } from 'react-toastify'
import { useAuth } from '../../../auth'
import {
  listPartAttachments,
  uploadPartAttachments,
  deletePartAttachment,
  partAttachmentViewUrl,
  partAttachmentDownloadUrl,
} from '../../core/_requests'
import type { PartAttachmentInfo, PartDto } from '../../core/_models'
import { FileViewerModal } from '../../../QHSE/components/FileViewerModal'

interface Props {
  visible: boolean
  part: PartDto
  onClose: () => void
  onSuccess?: () => void
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'
const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 2100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

export const PartAttachmentsModal: FC<Props> = ({ visible, part, onClose, onSuccess }) => {
  const { auth } = useAuth()
  const token = auth?.auth?.jwt

  const [attachments, setAttachments] = useState<PartAttachmentInfo[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [viewingFile, setViewingFile] = useState<PartAttachmentInfo | null>(null)

  useEffect(() => {
    if (visible && part?.id) {
      loadAttachments()
    }
  }, [visible, part?.id])

  const title = useMemo(() => {
    const name = part?.name ? ` - ${part.name}` : ''
    return `Part Attachments${name}`
  }, [part?.name])

  const loadAttachments = async () => {
    if (!part?.id) return
    setLoading(true)
    try {
      const data = await listPartAttachments(part.id)
      setAttachments(data)
    } catch (error: any) {
      console.error('Error loading attachments:', error)
      toast.error('Failed to load attachments', { position: 'top-center' })
    } finally {
      setLoading(false)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setSelectedFiles(files)
  }

  const handleUpload = async () => {
    if (!part?.id) return
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload', { position: 'top-center' })
      return
    }

    setUploading(true)
    try {
      const updated = await uploadPartAttachments(part.id, selectedFiles)
      setAttachments(updated)
      setSelectedFiles([])
      toast.success(`Uploaded ${selectedFiles.length} file(s)`, { position: 'top-center' })
      onSuccess?.()
    } catch (error: any) {
      console.error('Error uploading attachments:', error)
      toast.error('Failed to upload attachments', { position: 'top-center' })
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (fileName: string) => {
    if (!part?.id) return
    if (!window.confirm('Are you sure you want to delete this attachment?')) {
      return
    }

    try {
      await deletePartAttachment(part.id, fileName)
      toast.success('Attachment deleted successfully', { position: 'top-center' })
      loadAttachments()
      onSuccess?.()
    } catch (error: any) {
      console.error('Error deleting attachment:', error)
      toast.error('Failed to delete attachment', { position: 'top-center' })
    }
  }

  const handleDownload = async (attachment: PartAttachmentInfo) => {
    if (!part?.id) return
    try {
      const url = partAttachmentDownloadUrl(part.id, attachment.fileName)
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })
      if (!res.ok) {
        throw new Error(`Download failed: ${res.status}`)
      }
      const blob = await res.blob()
      const downloadUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = attachment.fileName || 'download'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(downloadUrl)
    } catch (error: any) {
      console.error('Error downloading attachment:', error)
      toast.error('Failed to download attachment', { position: 'top-center' })
    }
  }

  const formatBytes = (n?: number) => {
    const v = Number(n ?? 0)
    if (!Number.isFinite(v) || v <= 0) return '-'
    const units = ['B', 'KB', 'MB', 'GB']
    let size = v
    let i = 0
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024
      i++
    }
    const digits = i === 0 ? 0 : 1
    return `${size.toFixed(digits)} ${units[i]}`
  }

  if (!visible) return null

  return (
    <>
      <div style={WRAP_STYLE} tabIndex={-1} onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div
          className='bg-white text-dark rounded shadow-lg d-flex flex-column'
          style={{
            width: '820px',
            maxWidth: '92vw',
            maxHeight: '90vh',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
            <h5 className='modal-title text-dark m-0'>{title}</h5>
            <button type='button' className='btn-close' onClick={onClose}></button>
          </div>

          <div className='flex-grow-1 overflow-auto px-4 py-3'>
            {/* Upload Section */}
            <div className='card bg-light mb-4'>
              <div className='card-body'>
                <h6 className='fw-bold mb-3'>Upload New Attachments</h6>
                <div className='mb-3'>
                  <label className={LABEL}>Select Files</label>
                  <input
                    type='file'
                    className='form-control'
                    multiple
                    onChange={handleFileSelect}
                    disabled={uploading}
                  />
                  {selectedFiles.length > 0 && (
                    <div className='mt-2 text-muted fs-7'>
                      {selectedFiles.length} file(s) selected
                    </div>
                  )}
                </div>
                <button
                  type='button'
                  className='btn btn-primary btn-sm'
                  onClick={handleUpload}
                  disabled={selectedFiles.length === 0 || uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>

            {/* Attachments List */}
            <div>
              <h6 className='fw-bold mb-3'>Attachments ({attachments.length})</h6>
              {loading ? (
                <div className='text-center py-5'>
                  <div className='spinner-border' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                </div>
              ) : attachments.length === 0 ? (
                <div className='text-muted text-center py-4'>No attachments found</div>
              ) : (
                <div className='table-responsive'>
                  <table className='table table-bordered align-middle'>
                    <thead>
                      <tr>
                        <th>File Name</th>
                        <th style={{ width: '140px' }}>Size</th>
                        <th style={{ width: '180px' }}>Type</th>
                        <th style={{ width: '140px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attachments.map((attachment) => (
                        <tr key={attachment.fileName}>
                          <td>{attachment.fileName}</td>
                          <td>{formatBytes(attachment.sizeBytes)}</td>
                          <td>{attachment.mimeType || '-'}</td>
                          <td>
                            <div className='d-flex gap-1'>
                              <button
                                className='btn btn-sm btn-light'
                                onClick={() => setViewingFile(attachment)}
                                title='View'
                              >
                                <KTSVG path='/media/icons/duotune/general/gen005.svg' className='svg-icon-2' />
                              </button>
                              <button
                                className='btn btn-sm btn-light'
                                onClick={() => handleDownload(attachment)}
                                title='Download'
                              >
                                <KTSVG path='/media/icons/duotune/arrows/arr076.svg' className='svg-icon-2' />
                              </button>
                              <button
                                className='btn btn-sm btn-light-danger'
                                onClick={() => handleDelete(attachment.fileName)}
                                title='Delete'
                              >
                                <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-2' />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className='border-top d-flex justify-content-end gap-2 px-4 py-3 flex-shrink-0'>
            <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>

      {viewingFile && part?.id && (
        <FileViewerModal
          visible={true}
          viewUrl={partAttachmentViewUrl(part.id, viewingFile.fileName)}
          downloadUrl={partAttachmentDownloadUrl(part.id, viewingFile.fileName)}
          fileName={viewingFile.fileName}
          onClose={() => setViewingFile(null)}
        />
      )}
    </>
  )
}

export default PartAttachmentsModal
